/**
 * Document Upload Hook with Redaction Support
 * BRD-014: Client-Side Document Redaction
 *
 * This hook implements the 3-step presigned URL upload pattern:
 * 1. POST /documents/upload-url → Get presigned URL and document ID
 * 2. PUT to presigned URL → Upload file directly to MinIO/S3
 * 3. POST /documents/upload-complete → Confirm upload with optional redaction metadata
 *
 * Usage:
 *   const { uploadWithRedaction, uploadProgress, isUploading } = useUploadWithRedaction();
 *
 *   // Upload without redaction
 *   await uploadWithRedaction({ file, category: 'insurance' });
 *
 *   // Upload with redaction metadata
 *   await uploadWithRedaction({
 *     file: redactedPdfBlob,
 *     category: 'insurance',
 *     redactionMetadata: {
 *       redactions: [...],
 *       templateId: 'template-1',
 *       originalHash: 'sha256...',
 *     },
 *   });
 */

import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@myvault/shared';
import { customFetch, getUploadUrlV1DocumentsUploadUrlPost } from '@myvault/api-client';
import type { UploadUrlRequest } from '@myvault/api-client';
import { documentKeys } from './useDocuments';

// =============================================================================
// Types
// =============================================================================

/**
 * Individual redaction box from client-side redaction.
 * Coordinates are normalized (0-1 range) relative to page dimensions.
 */
export interface RedactionBox {
  id: string;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  category: string; // BSN, IBAN, EMAIL, PHONE, etc.
  type: 'manual' | 'auto-detected';
  confidence?: number;
}

/**
 * Redaction metadata to include when uploading a redacted document.
 * BRD-014 US-204: Upload Only Redacted Version
 */
export interface RedactionMetadata {
  templateId?: string;
  templateVersion?: string;
  redactions: RedactionBox[];
  originalHash?: string;
}

export interface UploadOptions {
  file: File | Blob;
  filename?: string;
  category?: string;
  classification?: 'public' | 'internal' | 'confidential' | 'restricted';
  tags?: string[];
  notes?: string;
  redactionMetadata?: RedactionMetadata;
  onProgress?: (progress: number) => void;
}

export interface UploadResult {
  documentId: string;
  status: string;
  message: string;
  redactionApplied: boolean;
}

interface UploadCompleteRequest {
  documentId: string;
  storagePath: string;
  filename: string;
  originalFilename?: string;
  checksumSha256?: string;
  classification?: string;
  category?: string;
  tags?: string[];
  notes?: string;
  redactionMetadata?: {
    templateId?: string;
    templateVersion?: string;
    redactions: Array<{
      id: string;
      pageNumber: number;
      x: number;
      y: number;
      width: number;
      height: number;
      category: string;
      type: string;
      confidence?: number;
    }>;
    originalHash?: string;
  };
}

interface UploadCompleteResponse {
  documentId: string;
  status: string;
  message: string;
  redactionApplied: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate SHA-256 hash of a file
 */
async function calculateSha256(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Upload file directly to presigned URL with progress tracking
 */
async function uploadToPresignedUrl(
  url: string,
  file: Blob,
  contentType: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}

/**
 * Complete the upload by notifying the backend
 */
async function completeUpload(data: UploadCompleteRequest): Promise<UploadCompleteResponse> {
  return customFetch<UploadCompleteResponse>({
    url: '/api/v1/documents/upload-complete',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data,
  });
}

// =============================================================================
// Hook
// =============================================================================

export function useUploadWithRedaction() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const mutation = useMutation({
    mutationFn: async (options: UploadOptions): Promise<UploadResult> => {
      const {
        file,
        filename,
        category,
        classification,
        tags,
        notes,
        redactionMetadata,
        onProgress,
      } = options;

      // Determine filename and content type
      const actualFilename = filename || (file instanceof File ? file.name : 'document.pdf');
      const contentType = file.type || 'application/pdf';

      // Reset progress
      setUploadProgress(0);

      // Step 1: Get presigned upload URL
      const uploadUrlRequest: UploadUrlRequest = {
        filename: actualFilename,
        contentType: contentType,
        fileSize: file.size,
      };

      const uploadUrlResponse = await getUploadUrlV1DocumentsUploadUrlPost(uploadUrlRequest);

      // Access camelCase properties from generated response
      const uploadUrl = uploadUrlResponse.uploadUrl;
      const documentId = uploadUrlResponse.documentId;

      if (!uploadUrl || !documentId) {
        throw new Error('Invalid upload URL response from server');
      }

      // Step 2: Upload directly to presigned URL
      const handleProgress = (progress: number) => {
        setUploadProgress(progress);
        onProgress?.(progress);
      };

      await uploadToPresignedUrl(
        uploadUrl,
        file,
        contentType,
        handleProgress
      );

      // Step 3: Calculate checksum and complete upload
      const checksum = await calculateSha256(file);

      // Prepare redaction metadata in snake_case for API
      const apiRedactionMetadata = redactionMetadata
        ? {
            templateId: redactionMetadata.templateId,
            templateVersion: redactionMetadata.templateVersion,
            redactions: redactionMetadata.redactions.map((box) => ({
              id: box.id,
              pageNumber: box.pageNumber,
              x: box.x,
              y: box.y,
              width: box.width,
              height: box.height,
              category: box.category,
              type: box.type,
              confidence: box.confidence,
            })),
            originalHash: redactionMetadata.originalHash,
          }
        : undefined;

      const completeRequest: UploadCompleteRequest = {
        documentId: documentId,
        storagePath: '',  // Storage path is handled by backend based on document_id
        filename: actualFilename,
        originalFilename: actualFilename,
        checksumSha256: checksum,
        classification,
        category,
        tags,
        notes,
        redactionMetadata: apiRedactionMetadata,
      };

      const result = await completeUpload(completeRequest);

      return {
        documentId: result.documentId,
        status: result.status,
        message: result.message,
        redactionApplied: result.redactionApplied,
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentKeys.stats() });

      if (data.redactionApplied) {
        success('Document geüpload', 'Document met redactie succesvol geüpload');
      } else {
        success('Document geüpload', 'Document succesvol geüpload');
      }
    },
    onError: (err) => {
      setUploadProgress(0);
      showError(
        'Upload mislukt',
        err instanceof Error ? err.message : 'Er is een fout opgetreden bij het uploaden'
      );
    },
  });

  const uploadWithRedaction = useCallback(
    (options: UploadOptions) => mutation.mutateAsync(options),
    [mutation]
  );

  return {
    uploadWithRedaction,
    uploadProgress,
    isUploading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/**
 * Options for batch uploading multiple files
 */
export interface BatchUploadOptions {
  files: Array<{
    file: File | Blob;
    filename?: string;
    redactionMetadata?: RedactionMetadata;
  }>;
  category?: string;
  classification?: 'public' | 'internal' | 'confidential' | 'restricted';
  tags?: string[];
}

/**
 * Hook for batch uploading multiple files with optional redaction
 */
export function useUploadMultipleWithRedaction() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());

  const mutation = useMutation({
    mutationFn: async (options: BatchUploadOptions): Promise<UploadResult[]> => {
      const { files, category, classification, tags } = options;
      const results: UploadResult[] = [];

      for (const { file, filename, redactionMetadata } of files) {
        const fileId = filename || (file instanceof File ? file.name : `file-${Date.now()}`);

        const actualFilename = filename || (file instanceof File ? file.name : 'document.pdf');
        const contentType = file.type || 'application/pdf';

        // Step 1: Get presigned URL
        const uploadUrlResponse = await getUploadUrlV1DocumentsUploadUrlPost({
          filename: actualFilename,
          contentType: contentType,
          fileSize: file.size,
        });

        const uploadUrl = uploadUrlResponse.uploadUrl;
        const documentId = uploadUrlResponse.documentId;

        if (!uploadUrl || !documentId) {
          throw new Error(`Failed to get upload URL for ${actualFilename}`);
        }

        // Step 2: Upload to presigned URL
        await uploadToPresignedUrl(
          uploadUrl,
          file,
          contentType,
          (progress) => {
            setUploadProgress((prev) => new Map(prev).set(fileId, progress));
          }
        );

        // Step 3: Calculate checksum and complete
        const checksum = await calculateSha256(file);

        const apiRedactionMetadata = redactionMetadata
          ? {
              templateId: redactionMetadata.templateId,
              templateVersion: redactionMetadata.templateVersion,
              redactions: redactionMetadata.redactions.map((box) => ({
                id: box.id,
                pageNumber: box.pageNumber,
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
                category: box.category,
                type: box.type,
                confidence: box.confidence,
              })),
              originalHash: redactionMetadata.originalHash,
            }
          : undefined;

        const result = await completeUpload({
          documentId: documentId,
          storagePath: '',  // Storage path is handled by backend based on document_id
          filename: actualFilename,
          originalFilename: actualFilename,
          checksumSha256: checksum,
          classification,
          category,
          tags,
          redactionMetadata: apiRedactionMetadata,
        });

        results.push({
          documentId: result.documentId,
          status: result.status,
          message: result.message,
          redactionApplied: result.redactionApplied,
        });
      }

      return results;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentKeys.stats() });

      const redactedCount = data.filter((r) => r.redactionApplied).length;
      if (redactedCount > 0) {
        success(
          'Documenten geüpload',
          `${data.length} document(en) geüpload, waarvan ${redactedCount} met redactie`
        );
      } else {
        success('Documenten geüpload', `${data.length} document(en) succesvol geüpload`);
      }
    },
    onError: (err) => {
      setUploadProgress(new Map());
      showError(
        'Upload mislukt',
        err instanceof Error ? err.message : 'Er is een fout opgetreden bij het uploaden'
      );
    },
  });

  return {
    uploadMultiple: useCallback(
      (options: BatchUploadOptions) => mutation.mutateAsync(options),
      [mutation]
    ),
    uploadProgress,
    isUploading: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    reset: () => {
      mutation.reset();
      setUploadProgress(new Map());
    },
  };
}
